import { Injectable, signal } from '@angular/core';

/**
 * How long a non-persistent toast stays on screen. Long enough to read two lines of Thai at a
 * glance, short enough that a stale failure notice does not follow the user to the next page.
 * Exported so specs can reason about the boundary instead of hard-coding a magic number.
 */
export const TOAST_AUTO_DISMISS_MS = 6000;

/**
 * Ceiling on simultaneously visible toasts. A page that fans out a dozen reads against a server
 * that has just died would otherwise paper over the UI with a wall of identical notices; the
 * de-duplication below already collapses *identical* copy, and this caps what is left.
 *
 * <p>It is a ceiling on the *screen*, not a licence to throw away a notice the user has to act on
 * — see {@link ToastService.capped} for which toast gives way.
 */
export const MAX_VISIBLE_TOASTS = 3;

/** What a caller asks for. `persistent` defaults to false — auto-dismiss is the ordinary case. */
export interface ToastInput {
  readonly title: string;
  readonly text: string;
  /** True for a notice the user must acknowledge — it never self-dismisses. */
  readonly persistent?: boolean;
}

/**
 * A toast currently on screen. `id` is both the render key and the handle for
 * {@link ToastService.dismiss}.
 */
export interface Toast extends Required<ToastInput> {
  readonly id: number;
}

/**
 * The portal's own toast stack, rendered by `ToastHost`.
 *
 * <p><b>Why not SweetAlert2.</b> Swal keeps a *single global instance*: firing while another Swal
 * is open REPLACES what is on screen instead of stacking. That is fine for one confirm dialog at a
 * time, and wrong for a notification channel — a 5xx toast would wipe out the "เซสชันหมดอายุ"
 * modal, and an interceptor's `catchError` (which always runs before the subscriber's `error:`
 * callback) would open a toast only for the page's own dialog to destroy it mid-animation. Owning
 * the stack removes that cause outright: nothing in this app can destroy a toast, and toasts stack
 * with each other. SweetAlert2 still runs the confirm dialogs and the session-expired modal; this
 * service simply renders above them.
 *
 * <p>The old code also carried a `setTimeout` deferral and an `isVisible()` guard. Do not read the
 * paragraph above as saying those are gone — {@link errorInterceptor} keeps a deferral and a
 * narrower stand-down for a *different* reason (not to survive Swal, but to avoid duplicating a
 * report the page already made). Their justification lives on that interceptor; this service is
 * unconditional and shows whatever it is handed.
 *
 * <p>Nothing here is session-shaped — no `AuthService`, no injected state at all. The portal serves
 * anonymous visitors on public routes (address lookup and master-data fire before login), so the
 * toast has to work with no user just as well as with one.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly items = signal<readonly Toast[]>([]);

  /** Live auto-dismiss timers, keyed by toast id, so a manual dismiss can cancel its own. */
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /**
   * Monotonic id source. Deliberately a counter and not `Math.random()`: ids are `@for` track
   * keys and timer keys, so a collision would make Angular reuse the wrong DOM node and let one
   * toast's timer close another. A counter cannot collide.
   */
  private nextId = 1;

  /** The visible toasts, newest first — the order the host paints them in. */
  readonly toasts = this.items.asReadonly();

  /**
   * Shows a toast, or refreshes the matching one already on screen.
   *
   * <p><b>De-duplication.</b> Five parallel reads hitting a dead server produce five identical
   * failures, and five stacked copies of the same sentence say nothing the first one did not.
   * A toast whose title *and* text already match is therefore not added again; instead its
   * auto-dismiss window restarts, so the notice stays up as long as the failures keep coming.
   */
  show(input: ToastInput): void {
    const persistent = input.persistent ?? false;
    const existing = this.items().find(
      (toast) => toast.title === input.title && toast.text === input.text,
    );

    if (existing) {
      // Persistence only ever ratchets up: if the same copy arrives once as a read failure and
      // once as a write failure, the "your data was not saved" reading is the one to keep.
      const refreshed: Toast = { ...existing, persistent: existing.persistent || persistent };
      if (refreshed.persistent !== existing.persistent) {
        this.items.update((current) =>
          current.map((toast) => (toast.id === existing.id ? refreshed : toast)),
        );
      }
      this.arm(refreshed);
      return;
    }

    const toast: Toast = { id: this.nextId++, title: input.title, text: input.text, persistent };

    this.items.set(this.capped([toast, ...this.items()]));
    this.arm(toast);
  }

  /**
   * Trims the stack back to {@link MAX_VISIBLE_TOASTS}, choosing what leaves rather than just
   * lopping off the end.
   *
   * <p><b>Persistent toasts go last.</b> A "ข้อมูลที่คุณกรอกยังไม่ถูกบันทึก" notice exists precisely
   * because it must not vanish without the user acknowledging it; letting a later read failure —
   * which will itself fade in six seconds — silently evict it would break the one invariant the
   * `persistent` flag is for. So the oldest *non-persistent* toast gives way first, and a
   * persistent one is dropped only when every survivor is persistent too, because the ceiling
   * still has to hold.
   *
   * <p>The toast just added (index 0) is never the one evicted: a failure discarded before it is
   * ever painted has not been reported at all.
   */
  private capped(stack: readonly Toast[]): readonly Toast[] {
    const kept = [...stack];

    while (kept.length > MAX_VISIBLE_TOASTS) {
      // `kept.length - 1` is at least 1 here, so both the search and the fallback stay clear of
      // the newest toast.
      let index = kept.length - 1;
      for (let i = kept.length - 1; i >= 1; i--) {
        if (!kept[i].persistent) {
          index = i;
          break;
        }
      }

      // Anything pushed off the screen takes its timer with it — otherwise it would fire later
      // against a toast nobody can see.
      const [dropped] = kept.splice(index, 1);
      this.clearTimer(dropped.id);
    }

    return kept;
  }

  /** Takes one toast down — the close button, and the auto-dismiss timer, both land here. */
  dismiss(id: number): void {
    this.clearTimer(id);
    this.items.update((current) => current.filter((toast) => toast.id !== id));
  }

  /**
   * Arms (or re-arms) one toast's auto-dismiss. Persistent toasts get no timer at all: the close
   * button is their only way out, which is the point — a failed save must not quietly disappear
   * and leave the user believing their work went through.
   */
  private arm(toast: Toast): void {
    this.clearTimer(toast.id);

    if (toast.persistent) {
      return;
    }

    this.timers.set(
      toast.id,
      setTimeout(() => {
        this.timers.delete(toast.id);
        this.items.update((current) => current.filter((item) => item.id !== toast.id));
      }, TOAST_AUTO_DISMISS_MS),
    );
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
